'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateRange } from '@daypicker/react';

import styles from './booking-widget.module.scss';

import BookingRooms from './_booking-widget-rooms';
import BookingPromoCodeComponent from './_booking-widget-promocode';
import BookingCalendar from './_booking-widget-calendar';

import Icons from '@components/Icons';
import Button from '@components/SectionComponents/Buttons/Button';
import type { roomQty } from '@lib/types';

const formatDate = (d: Date): string => {
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return [year, month, day].join('-');
};

const addDays = (date: Date, days: number): Date => {
    let result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

const urlParamsConfig = {
    hotel: '51603',
    bookingUrl: 'https://bookings.thecrocodilehunterlodge.com.au/rates-room1',
}; // Example: ?hotel=5160&arrival=2022-04-28&departure=2022-04-29&rooms=2&adults[1]=2&adults[2]=1&children[1]=2&children[2]=3code=xyz

const roomConfig = {
    limits: {
        rooms: {
            min: 1,
            max: 7,
        },
        guests: {
            //guest limits should be implemented here
            adults: {
                min: 1,
                max: 6,
            },
            children: {
                min: 0,
                max: 4,
            },
            maxguests: 6,
        },
    },
    numToWord: ['one', 'two', 'three', 'four', 'five', 'six', 'seven'],
};

const BookingComponent = () => {
    // Open/close date and room dropdowns
    const [openDates, setOpenDates] = useState(false);
    const [openRooms, setOpenRooms] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Mobile Popup
    const [showMobileBooking, setShowMobileBooking] = useState<boolean>(false);

    const toggleShowMobileBooking = useCallback(() => {
        setShowMobileBooking(!showMobileBooking);
    }, [setShowMobileBooking, showMobileBooking]);

    const toggleShowMobileBookingBackground = useCallback(
        (e: any) => {
            e.stopPropagation();
            if (e.target === e.currentTarget) {
                setShowMobileBooking(!showMobileBooking);
            }
        },
        [setShowMobileBooking, showMobileBooking],
    );

    //Create Calendar date range state
    const [dateRange, setDateRange] = useState<DateRange | undefined>(
        undefined,
    );

    // function to convert dateRange to strings for url params
    const dateRangeStrings = useMemo(
        () => ({
            from: dateRange?.from ? formatDate(dateRange.from) : '',
            to: dateRange?.to ? formatDate(dateRange.to) : '',
        }),
        [dateRange],
    );

    // Returns a room object with the minimum number of guests
    const newDefaultMinimalRoom = useCallback(() => {
        return {
            adults: roomConfig.limits.guests.adults.min,
            children: roomConfig.limits.guests.children.min,
        };
    }, []);

    const [roomQty, setRoomQty] = useState([newDefaultMinimalRoom()]);

    // PromoCode setup
    const [promoCode, setPromoCode] = useState<string>('');

    // Convert URL params into an actual booking URL
    const convertedBookingURL = useMemo(() => {
        let adultsString: string = ''; // initialise adults string
        let childrenString: string = ''; // initialise children string

        // populate adults and children arrays with room data
        roomQty.forEach((room: roomQty, index: number) => {
            const roomNum = index + 1;
            adultsString += `&adults%5B${roomNum}%5D=${room.adults.toString()}`;
            childrenString += `&children%5B${roomNum}%5D=${room.children.toString()}`;

            let childArrayNum = 1;
            for (let i = 0; i < room.children; i++) {
                childrenString += `&childAge%5B${roomNum}%5D%5B${childArrayNum}%5D=16`;
                childArrayNum++;
            }
        });

        // Return full booking url with parameters
        return `${urlParamsConfig.bookingUrl}/?hotel=${urlParamsConfig.hotel}&arrival=${dateRangeStrings.from}&departure=${dateRangeStrings.to}&rooms=${roomQty.length}${adultsString}${childrenString}&codeType=promo&code=${promoCode}`;
        // https://bookings.thecrocodilehunterlodge.com.au/rates-room1?hotel=51603&arrival=2022-08-10&departure=2022-08-13&rooms=3&adults%5B1%5D=2&adults%5B2%5D=4&adults%5B3%5D=1&children%5B1%5D=2&childAge%5B1%5D%5B1%5D=16&childAge%5B1%5D%5B2%5D=16&children%5B2%5D=0&children%5B3%5D=3&childAge%5B3%5D%5B1%5D=16&childAge%5B3%5D%5B2%5D=16&childAge%5B3%5D%5B3%5D=16&codeType=promo&code=5
    }, [roomQty, promoCode, dateRangeStrings]);

    useEffect(() => {
        // Set default date range to 7 days from now to 10 days from now
        setDateRange({
            from: addDays(new Date(), 7),
            to: addDays(new Date(), 10),
        });
    }, []);

    // useEffect to close dropdowns when clicking outside of the component
    useEffect(() => {
        if (!openDates && !openRooms) return;
        const onPointerDown = (e: PointerEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) {
                setOpenDates(false);
                setOpenRooms(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [openDates, openRooms]);

    return (
        <>
            <div className={styles.bookingContainer}>
                {!showMobileBooking && (
                    <div
                        className={` ${styles.bookingWrapper}  ${styles.showDesktop}`}
                        ref={wrapperRef}
                    >
                        <BookingCalendar
                            open={openDates}
                            setOpen={setOpenDates}
                            dateRange={dateRange}
                            setRangeFunc={(range) => {
                                setDateRange(range);
                            }}
                            dateRangeStrings={dateRangeStrings}
                        />

                        <BookingRooms
                            open={openRooms}
                            setOpen={setOpenRooms}
                            roomQty={roomQty}
                            config={roomConfig}
                            setRoomQtyFunc={(qty) => {
                                setRoomQty(qty);
                            }}
                            newDefaultMinimalRoomFunc={newDefaultMinimalRoom}
                        />

                        {/* <BookingPromoCodeComponent
                            promoCode={promoCode}
                            isPopup={false}
                            setPromoCodeFunc={(promo: string) => {
                                setPromoCode(promo);
                            }}
                        /> */}

                        <div className={styles.fieldWrapper}>
                            <Button
                                href={convertedBookingURL}
                                text="CHECK AVAILABILITY"
                                target="_blank"
                                extraClasses={['wider']}
                            ></Button>
                        </div>
                    </div>
                )}

                <div
                    className={`${styles.fieldWrapper} ${styles.showMobile} ${styles.buttonMargin}`}
                >
                    <Button
                        href="#"
                        text="CHECK AVAILABILITY"
                        extraClasses={['wider']}
                        onClick={() => {
                            toggleShowMobileBooking();
                        }}
                    ></Button>
                </div>
            </div>
            {showMobileBooking && (
                <div
                    className={styles.bookingPopupBackground}
                    onClick={(e) => {
                        toggleShowMobileBookingBackground(e);
                    }}
                >
                    <div className={styles.bookingPopup} ref={wrapperRef}>
                        <div className={styles.closeContainer}>
                            <button
                                onClick={() => {
                                    toggleShowMobileBooking();
                                }}
                                aria-label="Close Booking"
                                className={styles.closeButton}
                            >
                                <span className={styles.promoText}>Close </span>
                                <Icons
                                    iconClass={`nav-x`}
                                    extraClasses={[styles.close]}
                                />
                            </button>
                        </div>

                        <BookingCalendar
                            open={openDates}
                            setOpen={setOpenDates}
                            dateRange={dateRange}
                            setRangeFunc={(range) => {
                                setDateRange(range);
                            }}
                            dateRangeStrings={dateRangeStrings}
                            isPopup={true}
                        />

                        <BookingRooms
                            open={openRooms}
                            setOpen={setOpenRooms}
                            roomQty={roomQty}
                            config={roomConfig}
                            setRoomQtyFunc={(qty) => {
                                setRoomQty(qty);
                            }}
                            newDefaultMinimalRoomFunc={newDefaultMinimalRoom}
                        />

                        {/* <BookingPromoCodeComponent
                            promoCode={promoCode}
                            isPopup={true}
                            setPromoCodeFunc={(promo: string) => {
                                setPromoCode(promo);
                            }}
                        /> */}

                        <Button
                            href={convertedBookingURL}
                            text="CHECK AVAILABILITY"
                            target="_blank"
                            extraClasses={['wider']}
                        ></Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default BookingComponent;
